import { useCallback } from "react";
import { isHotkeyPressed } from "react-hotkeys-hook";
import { useNavigate } from "react-router-dom";

import { resolveStepUrl } from "~/components/build/Show/lib/urlForView";
import { useBuild } from "~/components/build/Show/lib/BuildContext";
import { useBuildPreferencesStore } from "~/components/build/Show/lib/useBuildPreferencesStore";
import { Step } from "~/components/build/Show/lib/types";
import { inputStepUnblockDialogPath } from "~/components/Playground/Node/InputStepNode";
import { isIgnored, isWaitingForInput } from "~/lib/pipeline";

import {
  DisplayMode,
  getRelevantFilterForStep,
  useFilterStore,
} from "~/components/build/Show/lib/useFilterStore";

export function useStepSearch() {
  const navigate = useNavigate();

  const displayMode = useFilterStore((state) => state.displayMode);
  const expandState = useBuildPreferencesStore((state) => state.expandState);

  const { build } = useBuild();

  const selectStep = useCallback(
    (step: Step): void => {
      // Open the unblock dialog when the step is still awaiting input. This
      // matches list-view input step clicks by using the latest job ID from
      // step statistics, which the step search endpoint loads for results.
      const unblockDialogPath =
        (step.type === "input" || step.type === "block") && isWaitingForInput(step)
          ? inputStepUnblockDialogPath(build, step)
          : undefined;

      // Navigate to the step. Skipped (ignored) steps don't open the drawer —
      // there's nothing useful to show — so they select without opening it,
      // the same as holding shift.
      const open = !isHotkeyPressed("shift") && !isIgnored(step);
      navigate(resolveStepUrl(step.uuid, { open }));

      if (unblockDialogPath) {
        Turbo.visit(unblockDialogPath, { frame: "unblock_dialog" });
      }

      // Ensure the step is visible in the sidebar
      if (displayMode === DisplayMode.State) {
        const state = getRelevantFilterForStep(step);

        if (state) {
          expandState(state);
        }
      }

      // Focus the step in the sidebar
      window.requestAnimationFrame(() => {
        const sidebarStep = document.querySelector<HTMLAnchorElement>("[aria-current=step]");

        if (sidebarStep) {
          sidebarStep.focus();
        } else {
          (document.activeElement as HTMLInputElement)?.blur();
        }
      });
    },
    [build, navigate, expandState, displayMode],
  );

  return { selectStep };
}
